import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Bell, HelpCircle, LogOut, Settings, User as UserIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { isNavPathActive, mostSpecificNavPath, ownerPath } from '@/lib/hosts';
import { useAuth } from '@/contexts/AuthContext';
import { useShop } from '@/contexts/ShopContext';
import { useVisibleNavItems } from '@/hooks/useVisibleNavItems';
import type { NavItem } from '@/lib/navigation';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { OwnerDropdownContent } from '@/components/owner-ui';
import { ShopSwitcher } from './ShopSwitcher';

interface MobileDrawerTwoPaneProps {
  onNavigate?: () => void;
}

const initials = (value: string | null | undefined): string => {
  if (!value) return '?';
  const parts = value.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
};

const isItemActive = (pathname: string, href: string): boolean =>
  isNavPathActive(pathname, ownerPath(href));

/**
 * Mobile drawer with HeyGen-style two-pane layout.
 *
 * - Left rail (64px) : icon-only nav items (logo top, items, divider, bottom utils, user avatar)
 * - Right panel (flex-1) : contextual sub-nav of the selected rail item
 *
 * Tap behavior :
 * - Item WITHOUT children → navigate + close sheet
 * - Item WITH children    → select it (right panel shows its children) ; no nav yet
 *
 * Default selection : the currently-active rail item (matches `pathname`) if it has children,
 * else the first rail item with children.
 */
export function MobileDrawerTwoPane({ onNavigate }: MobileDrawerTwoPaneProps) {
  const { pathname } = useLocation();
  const { t } = useTranslation(['navigation', 'common']);
  const { user, logout } = useAuth();
  const { currentShop, shops } = useShop();
  const navigate = useNavigate();
  const visibleItems = useVisibleNavItems();

  // Default selection : current route's parent item with children, else first item with children.
  const defaultSelectedKey = useMemo(() => {
    const activeWithChildren = visibleItems.find(
      (item) =>
        (item.children?.length ?? 0) > 0 &&
        (isItemActive(pathname, item.href) ||
          item.children?.some((c) => isItemActive(pathname, c.href))),
    );
    if (activeWithChildren) return activeWithChildren.labelKey;
    const firstWithChildren = visibleItems.find((item) => (item.children?.length ?? 0) > 0);
    return firstWithChildren?.labelKey ?? visibleItems[0]?.labelKey ?? null;
  }, [visibleItems, pathname]);

  const [selectedKey, setSelectedKey] = useState<string | null>(defaultSelectedKey);

  // Re-sync when route changes (e.g. user navigates inside an open drawer).
  useEffect(() => {
    setSelectedKey(defaultSelectedKey);
  }, [defaultSelectedKey]);

  const selected: NavItem | null =
    visibleItems.find((item) => item.labelKey === selectedKey) ?? null;
  const selectedChildren = selected?.children ?? [];
  const activeChildPath = mostSpecificNavPath(
    pathname,
    selectedChildren.map((child) => ownerPath(child.href)),
  );

  const handleRailItemClick = (item: NavItem) => {
    const hasChildren = (item.children?.length ?? 0) > 0;
    if (hasChildren) {
      setSelectedKey(item.labelKey);
    } else {
      navigate(ownerPath(item.href));
      onNavigate?.();
    }
  };

  const userName = user?.name ?? user?.email ?? '';
  const userEmail = user?.email ?? '';

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="grid h-full grid-cols-[64px_1fr] bg-white dark:bg-charcoal-900">
      {/* ============================================================
          LEFT RAIL — icons only, 64px
          ============================================================ */}
      <aside
        aria-label={t('navigation:dashboard')}
        className="flex h-full flex-col items-center border-r border-slate-200 px-1.5 py-3 dark:border-charcoal-700"
      >
        {/* Logo */}
        <Link
          to={ownerPath('/dashboard')}
          onClick={onNavigate}
          aria-label={t('navigation:go_to_dashboard', { defaultValue: 'Tableau de bord' })}
          className="relative mb-3 flex h-10 w-10 items-center justify-center rounded-xl shadow-[0_0_0_2px_rgba(163,230,53,0.18),0_8px_16px_-8px_rgba(163,230,53,0.5)]"
        >
          <img
            src="/brand/velko-appicon.svg"
            alt="Velko"
            width={40}
            height={40}
            className="block h-10 w-10 rounded-xl"
            draggable={false}
          />
          <span
            aria-hidden="true"
            className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-lime-400 dark:border-charcoal-900"
          />
        </Link>

        <span aria-hidden="true" className="mb-1 h-px w-8 bg-slate-200 dark:bg-charcoal-700" />

        {/* Nav items */}
        <nav
          className="flex flex-1 flex-col items-center gap-0.5 overflow-y-auto"
          aria-label={t('navigation:dashboard')}
        >
          {visibleItems.map((item, idx) => {
            const active = isItemActive(pathname, item.href);
            const itemChildren = item.children ?? [];
            const hasChildren = itemChildren.length > 0;
            const childActive = itemChildren.some((c) => isItemActive(pathname, c.href));
            const isSelected = selectedKey === item.labelKey;

            const divider = idx > 0 && item.labelKey === 'account' && (
              <span
                aria-hidden="true"
                className="my-1 h-px w-8 bg-slate-200 dark:bg-charcoal-700"
              />
            );

            return (
              <div key={item.href} className="flex flex-col items-center">
                {divider}
                <button
                  type="button"
                  onClick={() => handleRailItemClick(item)}
                  aria-current={active ? 'page' : undefined}
                  aria-pressed={hasChildren ? isSelected : undefined}
                  aria-label={t(`navigation:${item.labelKey}`)}
                  className={cn(
                    'relative flex h-11 w-11 items-center justify-center rounded-lg text-slate-500 transition-colors',
                    'hover:bg-slate-100 hover:text-charcoal-950 dark:text-slate-400 dark:hover:bg-charcoal-800 dark:hover:text-slate-50',
                    (active || childActive) &&
                      'bg-lime-50 text-charcoal-950 dark:bg-lime-500/12 dark:text-lime-300',
                    isSelected &&
                      !active &&
                      !childActive &&
                      'bg-slate-100 text-charcoal-950 dark:bg-charcoal-800 dark:text-slate-50',
                  )}
                >
                  {active || childActive ? (
                    <span
                      aria-hidden="true"
                      className="absolute -left-1.5 top-2.5 bottom-2.5 w-[3px] rounded-full bg-lime-400"
                    />
                  ) : null}
                  <item.icon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
                  {hasChildren ? (
                    <span
                      aria-hidden="true"
                      className={cn(
                        'absolute right-1 top-1 h-1 w-1 rounded-full',
                        isSelected ? 'bg-lime-500' : 'bg-slate-300 dark:bg-slate-600',
                      )}
                    />
                  ) : null}
                </button>
              </div>
            );
          })}
        </nav>

        {/* Bottom utilities */}
        <div className="mt-2 flex flex-col items-center gap-0.5 border-t border-slate-200 pt-2 dark:border-charcoal-700">
          <Link
            to={ownerPath('/settings/notifications')}
            onClick={onNavigate}
            aria-label={t('navigation:notifications', { defaultValue: 'Notifications' })}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-charcoal-950 dark:text-slate-400 dark:hover:bg-charcoal-800"
          >
            <Bell className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
          </Link>
          <Link
            to={ownerPath('/support')}
            onClick={onNavigate}
            aria-label={t('navigation:support', { defaultValue: 'Aide' })}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-charcoal-950 dark:text-slate-400 dark:hover:bg-charcoal-800"
          >
            <HelpCircle className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={userName}
                className="relative mt-1 flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 font-head text-[12px] font-semibold text-charcoal-950 transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-400 focus-visible:ring-offset-2 dark:bg-charcoal-700 dark:text-slate-50"
              >
                {initials(userName)}
                <span
                  aria-hidden="true"
                  className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full border-2 border-white bg-lime-400 dark:border-charcoal-900"
                />
              </button>
            </DropdownMenuTrigger>
            <OwnerDropdownContent side="top" align="end" sideOffset={8} className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{userName}</span>
                  <span className="text-xs text-muted-foreground">{userEmail}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to={ownerPath('/settings/profile')} onClick={onNavigate} className="gap-2">
                  <UserIcon className="size-3.5" />
                  {t('navigation:profile', { defaultValue: 'Profil' })}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={ownerPath('/settings/shop')} onClick={onNavigate} className="gap-2">
                  <Settings className="size-3.5" />
                  {t('navigation:shop_settings', { defaultValue: 'Réglages boutique' })}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onSelect={handleLogout}>
                <LogOut className="size-3.5" />
                {t('common:logout', { defaultValue: 'Se déconnecter' })}
              </DropdownMenuItem>
            </OwnerDropdownContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* ============================================================
          RIGHT PANEL — contextual sub-nav of selected rail item
          ============================================================ */}
      <section
        aria-label={selected ? t(`navigation:${selected.labelKey}`) : t('navigation:dashboard')}
        className="flex h-full flex-col overflow-y-auto px-4 py-4"
      >
        {selected ? (
          <>
            {/* Header */}
            <header className="mb-3 flex items-start gap-2.5 border-b border-slate-200 pb-3 dark:border-charcoal-700">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-lime-100 text-charcoal-950 dark:bg-lime-500/15 dark:text-lime-300">
                <selected.icon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
              </span>
              <div className="min-w-0 flex-1">
                <h2 className="truncate font-head text-[14px] font-bold text-charcoal-950 dark:text-slate-50">
                  {t(`navigation:${selected.labelKey}`)}
                </h2>
                {shops.length > 0 ? (
                  <ShopSwitcher variant="mobile-header" onNavigate={onNavigate} />
                ) : (
                  <p className="truncate font-mono text-[10px] uppercase tracking-[0.1em] text-slate-400 dark:text-slate-500">
                    {currentShop?.name ?? 'Velko'}
                  </p>
                )}
              </div>
            </header>

            {/* Sub-nav list — children only. The parent label is already shown
                in the header above, and most parents share their href with the
                first child anyway (e.g. account → /employees = employees → /employees),
                so a parent entry would be a duplicate link. */}
            <ul className="flex flex-col gap-0.5">
              {selectedChildren.map((child) => {
                const ChildIcon = child.icon;
                const isChildActive = ownerPath(child.href) === activeChildPath;
                return (
                  <li key={child.href}>
                    <Link
                      to={ownerPath(child.href)}
                      onClick={onNavigate}
                      className={cn(
                        'flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors',
                        'text-slate-600 hover:bg-slate-100 hover:text-charcoal-950 dark:text-slate-300 dark:hover:bg-charcoal-800 dark:hover:text-slate-50',
                        isChildActive &&
                          'bg-lime-50 text-charcoal-950 dark:bg-lime-500/12 dark:text-lime-300',
                      )}
                    >
                      {ChildIcon ? (
                        <ChildIcon
                          className={cn(
                            'h-3.5 w-3.5 shrink-0',
                            isChildActive
                              ? 'text-lime-600 dark:text-lime-400'
                              : 'text-slate-400 dark:text-slate-500',
                          )}
                          strokeWidth={1.75}
                          aria-hidden="true"
                        />
                      ) : null}
                      <span className="flex-1 truncate">{t(`navigation:${child.labelKey}`)}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </>
        ) : (
          <div className="flex flex-1 items-center justify-center text-center text-[12px] text-slate-400 dark:text-slate-500">
            {t('navigation:tap_an_item', {
              defaultValue: 'Touchez une icône à gauche pour explorer.',
            })}
          </div>
        )}
      </section>
    </div>
  );
}
